/**
 * Audio mute toggle (sc-3290 follow-up).
 *
 * Fixed-position DOM button anchored above the pause button. Tap
 * toggles `CabinetAudioBus.setMuted` — same flag the pause overlay's
 * "MUTE AUDIO" row toggles, so all surfaces stay in sync.
 *
 * Renders a speaker glyph: unmuted = `🔊`-style waves drawn in pure
 * CSS shapes (avoids relying on emoji fonts that look wrong on iOS
 * Safari); muted = the same speaker with a slash through it.
 *
 * Lifecycle parallels pause-button:
 *   - `attachMuteButton()` once at cabinet boot. The button lives for
 *     the entire cabinet session (mute is a cabinet-level setting, not
 *     a per-cartridge one — that's why this attaches separately from
 *     the launcher's pause-button attach).
 *   - `setMuteButtonVisible(visible)` to hide during press-start / pause
 *     overlay (same visibility lifecycle as the pause button).
 *   - `detachMuteButton()` rarely used; only on cabinet teardown.
 */

import { getCabinetAudioBus } from './context.js';

let state: {
  el: HTMLButtonElement;
  clickListener: (e: MouseEvent) => void;
} | null = null;

const BUTTON_ID = 'mute-button';

function ensureElement(): HTMLButtonElement {
  let el = document.getElementById(BUTTON_ID) as HTMLButtonElement | null;
  if (el) {
    if (el.parentElement !== document.body) {
      document.body.appendChild(el);
    }
    return el;
  }
  el = document.createElement('button');
  el.id = BUTTON_ID;
  el.type = 'button';
  el.className = 'mute-button';
  el.setAttribute('aria-label', 'Toggle audio');
  el.hidden = true;
  // Inner glyph wrapper — the speaker icon + a slash overlay for muted.
  // CSS shape (no emoji) so the rendering is consistent across browsers.
  const glyph = document.createElement('span');
  glyph.className = 'mute-button__glyph';
  el.appendChild(glyph);
  document.body.appendChild(el);
  return el;
}

function applyMutedState(el: HTMLButtonElement, muted: boolean): void {
  el.classList.toggle('is-muted', muted);
  el.setAttribute('aria-pressed', muted ? 'true' : 'false');
  el.setAttribute('aria-label', muted ? 'Unmute audio' : 'Mute audio');
}

export function attachMuteButton(): void {
  detachMuteButton();
  const el = ensureElement();
  const audio = getCabinetAudioBus();
  applyMutedState(el, audio.getMuted());
  const clickListener = (evt: MouseEvent): void => {
    evt.preventDefault();
    const next = !audio.getMuted();
    audio.setMuted(next);
    applyMutedState(el, next);
  };
  el.addEventListener('click', clickListener);
  el.hidden = false;
  state = { el, clickListener };
}

export function detachMuteButton(): void {
  if (!state) return;
  state.el.removeEventListener('click', state.clickListener);
  state.el.hidden = true;
  state = null;
}

/**
 * Show / hide without unwiring. Used during press-start + pause overlay
 * so the player can't queue mute changes mid-transition (the pause
 * overlay's own MUTE row covers that path while paused).
 */
export function setMuteButtonVisible(visible: boolean): void {
  if (!state) return;
  state.el.hidden = !visible;
  // Re-sync glyph in case the audio bus was toggled elsewhere (pause
  // overlay's MUTE row) while we were hidden.
  if (visible) {
    applyMutedState(state.el, getCabinetAudioBus().getMuted());
  }
}
